<template>
  <!--
    Page temporaire « Bientôt disponible » pour les Intégrations & la Marketplace.
    Elle ne réalise AUCUN appel backend : tant que cette partie est en cours de
    développement, les routes shop.Integrations et shop.IntegrationConfig pointent
    ici (cf. router/index.ts) afin d'éviter toute requête vers /integrations.
    Pour réactiver la fonctionnalité, rebrancher ShopIntegrations / IntegrationConfig
    dans le router.
  -->
  <div class="integrations-soon">
    <div class="soon-card">
      <div class="soon-badge">
        <span class="soon-badge-dot" />
        {{ t('integrations.comingSoon.badge') }}
      </div>

      <div class="soon-illustration">
        <i class="fas fa-puzzle-piece soon-icon soon-icon--main" />
        <i class="fas fa-store soon-icon soon-icon--floating soon-icon--1" />
        <i class="fas fa-plug soon-icon soon-icon--floating soon-icon--2" />
        <i class="fas fa-bolt soon-icon soon-icon--floating soon-icon--3" />
      </div>

      <h1 class="soon-title">
        {{ t('integrations.comingSoon.title') }}
      </h1>

      <!--
        Focus depuis un point d'entrée de la marketplace (CTA « installer »,
        lien widget) : `?focus=slug` (+ `focusName` optionnel). On affiche un
        message ciblé « L'intégration {name} arrive bientôt » — SANS aucun appel
        backend (la page reste 100 % statique par conception).
      -->
      <p
        v-if="focusName"
        class="soon-focus"
      >
        {{ t('integrations.comingSoon.focus', { name: focusName }) }}
      </p>

      <p class="soon-subtitle">
        {{ t('integrations.comingSoon.subtitle') }}
      </p>

      <p class="soon-description">
        {{ t('integrations.comingSoon.description') }}
      </p>

      <ul class="soon-features">
        <li class="soon-feature">
          <i class="fas fa-check soon-feature-icon" />
          {{ t('integrations.comingSoon.features.discover') }}
        </li>
        <li class="soon-feature">
          <i class="fas fa-check soon-feature-icon" />
          {{ t('integrations.comingSoon.features.connect') }}
        </li>
        <li class="soon-feature">
          <i class="fas fa-check soon-feature-icon" />
          {{ t('integrations.comingSoon.features.automate') }}
        </li>
      </ul>

      <router-link
        class="soon-back"
        :to="{ name: 'home' }"
      >
        <i class="fas fa-arrow-left" />
        {{ t('integrations.comingSoon.backHome') }}
      </router-link>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'IntegrationsComingSoon',

  setup() {
    const { t } = useI18n();
    const route = useRoute();

    // `focusName` (label lisible) est passé en query par les points d'entrée qui
    // connaissent le nom de l'intégration ; à défaut on retombe sur le slug
    // (`focus`). Aucun appel réseau : la résolution reste purement URL → écran.
    const focusName = computed<string>(() => {
      const name = route.query.focusName;
      const slug = route.query.focus;
      const raw = (Array.isArray(name) ? name[0] : name)
        ?? (Array.isArray(slug) ? slug[0] : slug);
      return raw ? String(raw) : '';
    });

    return { t, focusName };
  },
});
</script>

<style scoped lang="scss">
.integrations-soon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  padding: 2rem 1.5rem;
}

.soon-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 560px;
  width: 100%;
  padding: 3rem 2.5rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(17, 24, 39, 0.06);
}

/* Badge */
.soon-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  background: rgba(148, 200, 64, 0.12);
  color: #5d8a1e;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2rem;
}

.soon-badge-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #94c840;
  box-shadow: 0 0 0 0 rgba(148, 200, 64, 0.5);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(148, 200, 64, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(148, 200, 64, 0); }
  100% { box-shadow: 0 0 0 0 rgba(148, 200, 64, 0); }
}

/* Illustration */
.soon-illustration {
  position: relative;
  width: 140px;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.soon-icon--main {
  font-size: 3.5rem;
  color: #94c840;
  background: linear-gradient(135deg, rgba(148, 200, 64, 0.15) 0%, rgba(148, 200, 64, 0.05) 100%);
  width: 110px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 28px;
}

.soon-icon--floating {
  position: absolute;
  font-size: 1.125rem;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  color: #94c840;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.08);
}

.soon-icon--1 {
  top: -6px;
  right: 4px;
  animation: float 3s ease-in-out infinite;
}

.soon-icon--2 {
  bottom: 2px;
  left: -8px;
  color: #7fb32e;
  animation: float 3s ease-in-out infinite 0.6s;
}

.soon-icon--3 {
  bottom: 30px;
  right: -10px;
  color: #f59e0b;
  animation: float 3s ease-in-out infinite 1.2s;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* Text */
.soon-title {
  font-size: 1.625rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
  line-height: 1.2;
}

.soon-subtitle {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #94c840;
  margin: 0 0 1rem 0;
}

.soon-focus {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  background: rgba(148, 200, 64, 0.1);
  border: 1px solid rgba(148, 200, 64, 0.25);
  border-radius: 10px;
  padding: 0.625rem 1rem;
  margin: 0 0 1rem 0;
}

.soon-description {
  font-size: 0.9375rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 1.75rem 0;
  max-width: 440px;
}

/* Features */
.soon-features {
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  text-align: left;
}

.soon-feature {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  color: #374151;
}

.soon-feature-icon {
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(148, 200, 64, 0.12);
  color: #5d8a1e;
  border-radius: 50%;
  font-size: 0.625rem;
}

/* Back link */
.soon-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: #94c840;
  color: #ffffff;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background: #7fb32e;
    transform: translateY(-1px);
  }

  i {
    font-size: 0.75rem;
  }
}
</style>
